import { mcGetCloudItem, mcGetItem, mcSetCachedItem, mcSetCloudItem } from "./mcCloudSync";

export const MC_RESPONSE_DESIGN_KEY = "mc-response-design-config";
export const MC_RESPONSE_DESIGN_EVENT = "mc-response-design-config-updated";

export const DEFAULT_RESPONSE_DESIGN_CONFIG = {
  enabled: true,
  minimumTrigger: { exposure: 1, flaggedProducts: 1, mode: "either" },
  severity: {
    critical: { exposure: 2000, flaggedProducts: 15 },
    high: { exposure: 750, flaggedProducts: 8 },
    medium: { exposure: 200, flaggedProducts: 2 },
    low: { exposure: 0, flaggedProducts: 1 },
  },
  escalation: {
    critical: { operationsAfterMinutes: 15, ownerAfterMinutes: 30 },
    high: { operationsAfterMinutes: 60, ownerAfterMinutes: 180 },
    medium: { operationsAfterMinutes: 240, ownerAfterMinutes: 480 },
    low: { operationsAfterMinutes: 1440, ownerAfterMinutes: 0 },
  },
  verification: {
    required: true,
    role: "operations_manager",
    customProfile: "",
  },
};

let responseDesignCloudTimer = null;
let responseDesignPendingValue = "";

function num(value, fallback = 0) {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : fallback;
}

export function normalizeResponseDesignScope(scope = "ALL") {
  const raw = String(scope || "ALL").trim();
  if (!raw || raw.toUpperCase() === "ALL" || raw.toLowerCase() === "all sites") return "ALL";
  const withoutPrefix = raw
    .replace(/^inventory_/i, "")
    .replace(/(?:_|\s+)Store$/i, "")
    .trim();
  const clean = withoutPrefix
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
  return clean ? `inventory_${clean}` : "ALL";
}

export function normalizeResponseDesignConfig(value = {}) {
  const base = DEFAULT_RESPONSE_DESIGN_CONFIG;
  const severity = {};
  const escalation = {};
  ["critical", "high", "medium", "low"].forEach((key) => {
    severity[key] = {
      exposure: num(value?.severity?.[key]?.exposure, base.severity[key].exposure),
      flaggedProducts: num(value?.severity?.[key]?.flaggedProducts, base.severity[key].flaggedProducts),
    };
    escalation[key] = {
      operationsAfterMinutes: num(value?.escalation?.[key]?.operationsAfterMinutes, base.escalation[key].operationsAfterMinutes),
      ownerAfterMinutes: num(value?.escalation?.[key]?.ownerAfterMinutes, base.escalation[key].ownerAfterMinutes),
    };
  });
  return {
    enabled: value?.enabled !== false,
    minimumTrigger: {
      exposure: num(value?.minimumTrigger?.exposure, base.minimumTrigger.exposure),
      flaggedProducts: num(value?.minimumTrigger?.flaggedProducts, base.minimumTrigger.flaggedProducts),
      mode: value?.minimumTrigger?.mode === "both" ? "both" : "either",
    },
    severity,
    escalation,
    verification: {
      required: value?.verification?.required !== false,
      role: ["site_manager", "operations_manager", "owner", "same_profile", "custom_profile"].includes(value?.verification?.role)
        ? value.verification.role
        : base.verification.role,
      customProfile: String(value?.verification?.customProfile || "").trim(),
    },
  };
}

function normalizeResponseDesignRecord(value = null) {
  const source = value && typeof value === "object" ? value : {};

  // Backward compatibility: the original response-design document stored one
  // plain config. Treat that value as the All Sites default on first read/save.
  const isScopedRecord = source?.version === 2
    || (source?.global && typeof source.global === "object")
    || (source?.siteOverrides && typeof source.siteOverrides === "object");

  if (!isScopedRecord) {
    return {
      version: 2,
      global: normalizeResponseDesignConfig(source || DEFAULT_RESPONSE_DESIGN_CONFIG),
      siteOverrides: {},
      updatedAt: "",
    };
  }

  const siteOverrides = {};
  Object.entries(source.siteOverrides || {}).forEach(([scope, config]) => {
    const key = normalizeResponseDesignScope(scope);
    if (key !== "ALL") siteOverrides[key] = normalizeResponseDesignConfig(config);
  });

  return {
    version: 2,
    global: normalizeResponseDesignConfig(source.global || DEFAULT_RESPONSE_DESIGN_CONFIG),
    siteOverrides,
    updatedAt: String(source.updatedAt || ""),
  };
}

function loadResponseDesignRecord() {
  try {
    const parsed = JSON.parse(mcGetItem(MC_RESPONSE_DESIGN_KEY) || "null");
    return normalizeResponseDesignRecord(parsed);
  } catch (_) {
    return normalizeResponseDesignRecord(null);
  }
}

function queueResponseDesignCloudSave(value = "") {
  responseDesignPendingValue = String(value || "");
  if (typeof window === "undefined") return;
  if (responseDesignCloudTimer) window.clearTimeout(responseDesignCloudTimer);
  responseDesignCloudTimer = window.setTimeout(() => {
    responseDesignCloudTimer = null;
    const pending = responseDesignPendingValue;
    responseDesignPendingValue = "";
    if (!pending) return;
    try { mcSetCloudItem(MC_RESPONSE_DESIGN_KEY, pending).catch(() => {}); } catch (_) {}
  }, 250);
}

export function loadResponseDesignConfig(scope = "ALL") {
  const record = loadResponseDesignRecord();
  const key = normalizeResponseDesignScope(scope);
  if (key !== "ALL" && record.siteOverrides[key]) return normalizeResponseDesignConfig(record.siteOverrides[key]);
  return normalizeResponseDesignConfig(record.global);
}

export async function hydrateResponseDesignConfig(scope = "ALL") {
  try {
    const cloudRaw = await mcGetCloudItem(MC_RESPONSE_DESIGN_KEY);
    if (cloudRaw) {
      const parsed = JSON.parse(cloudRaw);
      const record = normalizeResponseDesignRecord(parsed);
      mcSetCachedItem(MC_RESPONSE_DESIGN_KEY, JSON.stringify(record));
      try {
        window.dispatchEvent(new CustomEvent(MC_RESPONSE_DESIGN_EVENT, {
          detail: { source: "cloud-refresh", scope: normalizeResponseDesignScope(scope) },
        }));
      } catch (_) {}
    }
  } catch (_) {}
  return loadResponseDesignConfig(scope);
}

export function hasResponseDesignOverride(scope = "ALL") {
  const key = normalizeResponseDesignScope(scope);
  if (key === "ALL") return false;
  const record = loadResponseDesignRecord();
  return Boolean(record.siteOverrides[key]);
}

export function saveResponseDesignConfig(config = {}, scope = "ALL") {
  const normalized = normalizeResponseDesignConfig(config);
  const key = normalizeResponseDesignScope(scope);
  const current = loadResponseDesignRecord();
  const next = {
    ...current,
    version: 2,
    global: key === "ALL" ? normalized : current.global,
    siteOverrides: key === "ALL"
      ? { ...(current.siteOverrides || {}) }
      : { ...(current.siteOverrides || {}), [key]: normalized },
    updatedAt: new Date().toISOString(),
  };
  const value = JSON.stringify(next);

  // Cost mode: update the local cache immediately, then coalesce rapid input
  // changes into one Firestore write. No realtime Firestore listener is used.
  try { mcSetCachedItem(MC_RESPONSE_DESIGN_KEY, value); } catch (_) {}
  queueResponseDesignCloudSave(value);
  try {
    window.dispatchEvent(new CustomEvent(MC_RESPONSE_DESIGN_EVENT, {
      detail: { scope: key, config: normalized },
    }));
  } catch (_) {}
  return normalized;
}

export function responseTriggerApplies(exposure = 0, flaggedProducts = 0, config = loadResponseDesignConfig()) {
  if (!config.enabled) return false;
  const exposurePass = Math.abs(Number(exposure) || 0) >= config.minimumTrigger.exposure;
  const flagsPass = Math.abs(Number(flaggedProducts) || 0) >= config.minimumTrigger.flaggedProducts;
  return config.minimumTrigger.mode === "both" ? exposurePass && flagsPass : exposurePass || flagsPass;
}

export function determineConfiguredSeverity(exposure = 0, flaggedProducts = 0, config = loadResponseDesignConfig()) {
  const absExposure = Math.abs(Number(exposure) || 0);
  const flags = Math.abs(Number(flaggedProducts) || 0);
  for (const key of ["critical", "high", "medium", "low"]) {
    const rule = config.severity[key];
    if (absExposure >= rule.exposure || flags >= rule.flaggedProducts) return key;
  }
  return "low";
}

export function buildEscalationPlan(severity = "low", createdAt = new Date().toISOString(), config = loadResponseDesignConfig()) {
  const rule = config.escalation[severity] || config.escalation.low;
  const created = new Date(createdAt).getTime();
  const at = (mins) => mins > 0 ? new Date(created + mins * 60000).toISOString() : null;
  return {
    stage: "site_manager",
    siteManagerAt: new Date(created).toISOString(),
    operationsManagerAt: at(rule.operationsAfterMinutes),
    ownerAt: at(rule.ownerAfterMinutes),
    operationsAfterMinutes: rule.operationsAfterMinutes,
    ownerAfterMinutes: rule.ownerAfterMinutes,
    highestNotified: "site_manager",
    complete: false,
  };
}
